import { useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ja';
import NoticeTypeIcon from './NoticeTypeIcons';
import { NOTICE_CATEGORIES, NOTICE_TYPES, REASONS } from '../../common/constants';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';

const noticeCategoryLabel = (noticeCategoryCode) => {
  switch (noticeCategoryCode) {
    case NOTICE_CATEGORIES.REGULATION.value:
      return NOTICE_CATEGORIES.REGULATION.label;
    case NOTICE_CATEGORIES.ATTENTION.value:
      return NOTICE_CATEGORIES.ATTENTION.label;
  };
};

const noticeTypeLabel = (noticeTypeCode) => {
  switch (noticeTypeCode) {
    case NOTICE_TYPES.THIRTY_FIVE_LIMIT.value:
      return NOTICE_TYPES.THIRTY_FIVE_LIMIT.label;
    case NOTICE_TYPES.TWENTY_FIVE_LIMIT.value:
      return NOTICE_TYPES.TWENTY_FIVE_LIMIT.label;
    case NOTICE_TYPES.SUSPENSION.value:
      return NOTICE_TYPES.SUSPENSION.label;
    case NOTICE_TYPES.LIMIT_CANCEL.value:
      return NOTICE_TYPES.LIMIT_CANCEL.label;
    case NOTICE_TYPES.SUSPENSION_CANCEL.value:
      return NOTICE_TYPES.SUSPENSION_CANCEL.label;
    case NOTICE_TYPES.SLOWDOWN.value:
      return NOTICE_TYPES.SLOWDOWN.label;
  };
};

const reasonLabel = (reasonCode) => {
  switch (reasonCode) {
    case REASONS.RAIN.value:
      return REASONS.RAIN.label;
    case REASONS.WIND.value:
      return REASONS.WIND.label;
    case REASONS.FLOOD.value:
      return REASONS.FLOOD.label;
    case REASONS.NULL.value:
      return REASONS.NULL.label;
  };
};

const NoticeTemplatesTable = (props) => {
  const templates = props.templates;
  const view = props.view;
  const columns = ['線区', '区間', '通告種類', '', '事由', '作成日時'];
  const { typography, palette } = useTheme();
  const styles = {
    noTemplateMessage: {
      ...typography.body16bold,
      color: palette.darkgray,
      align: 'center',
    },
  };

  dayjs.locale('ja');
  dayjs.extend(relativeTime);

  const isSelected = (rowId) => {
    return props.selectedTemplateIds.includes(rowId);
  };

  const toggleSelected = (rowId) => {
    isSelected(rowId) ? props.setSelectedTemplateIds(props.selectedTemplateIds.filter((selectedId) => selectedId !== rowId)) :
      props.setSelectedTemplateIds([...props.selectedTemplateIds, rowId]);
  };

  return (
    <Table>
      <TableHead sx={{ backgroundColor: palette.backgroundBlue, position: 'sticky', top: 0, zIndex: 1 }}>
        <TableRow>
          <TableCell padding="checkbox"></TableCell>
          {columns.map((column) => (
            <TableCell key={column} padding='none' sx={{ ...typography.body14bold, color: palette.darkgray, height: 40 }}>
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          {/* テンプレートデータがない場合の表示 */}
          {templates.length === 0 && view === 'template' && (
            <TableCell colSpan={7} align='center' height='390px'>
              <Typography sx={styles.noTemplateMessage}>まだ通告テンプレートが登録されていません</Typography>
              <Typography sx={styles.noTemplateMessage}>右上の「通告テンプレート登録」ボタンから</Typography>
              <Typography sx={styles.noTemplateMessage}>新しいテンプレートを作成してください</Typography>
            </TableCell>
          )}
          {templates.length === 0 && view === 'dialog' && (
            <TableCell colSpan={7} align='center' height='456px'>
              <Typography sx={styles.noTemplateMessage}>まだ通告テンプレートが登録されていません</Typography>
              <Typography sx={styles.noTemplateMessage}>「管理メニュー」から「通告テンプレート」タブを選択し</Typography>
              <Typography sx={styles.noTemplateMessage}>右上の「通告テンプレート登録」ボタンから</Typography>
              <Typography sx={styles.noTemplateMessage}>新しいテンプレートを作成してください</Typography>
            </TableCell>
          )}
        </TableRow>
        {templates.map((template) => {
          const isTemplateSelected = isSelected(template._id);
          return (
            <TableRow
              hover
              role="checkbox"
              tabIndex={-1}
              key={template._id}
              onClick={() => toggleSelected(template._id)}
              selected={isTemplateSelected}
              sx={{ height: '58px' }}
            >
              <TableCell padding="checkbox">
                <Checkbox checked={isTemplateSelected} sx={{ color: 'primary.main' }} />
              </TableCell>
              <TableCell padding='none'>{template.section.name}</TableCell>
              <TableCell padding='none'>{template.startStation.name}〜{template.endStation.name}</TableCell>
              <TableCell padding='none'>{noticeCategoryLabel(template.noticeCategoryCode)}</TableCell>
              <TableCell padding='none' sx={{ display: 'flex', alignItems: 'center', height: '58px', gap: '10px' }}>
                <NoticeTypeIcon noticeTypeCode={template.noticeTypeCode} sx={{ width: '24px', height: '24px' }} />
                {noticeTypeLabel(template.noticeTypeCode)}
              </TableCell>
              <TableCell padding='none'>{reasonLabel(template.reasonCode)}</TableCell>
              <TableCell padding='none'>{dayjs(template.createdAt).fromNow()}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default NoticeTemplatesTable;

NoticeTemplatesTable.propTypes = {
  templates: PropTypes.array,
  view: PropTypes.string,
  selectedTemplateIds: PropTypes.array,
  setSelectedTemplateIds: PropTypes.func,
};
